import { DashboardCategories } from "./dashboardMenu/DashboardCategories";
import { AddItem } from "./AddItem";
import { AddCategory } from "./AddCategory";
import { CustomerFacingMenuLink } from "./CustomerFacingMenuLink";
import React, { useEffect, useMemo, useState } from "react";
import { UpdateCurrency } from "./dashboardMenu/UpdateCurrency";
import { CategoryType } from "../../../client/menuClient";
import { useMenuData } from "../../../menu-provider/MenuDataProvider";
import { CategoryItems } from "./dashboardMenu/CategoryItems";
import {
  Box,
  FormControlLabel,
  IconButton,
  Paper,
  Popover,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import { GoLiveChecklist } from "../onboarding/GoLiveChecklist";
import SettingsIcon from "@mui/icons-material/Settings";
import CategoryIcon from "@mui/icons-material/Category";
import { usePostClientWithAuth } from "../../../client/postClientWithAuth";
import { Status } from "../../../client/status";

const EnableAllSwitch = ({ category }: { category: CategoryType }) => {
  const { postData: updateItemsStatus, status } = usePostClientWithAuth(
    `/api/category/${category.id}/enable-disable/`
  );
  const { reloadMenuData } = useMenuData();

  useEffect(() => {
    status == Status.Success && reloadMenuData();
  }, [reloadMenuData, status]);

  return (
    <FormControlLabel
      control={
        <Switch
          size="small"
          onChange={(event) =>
            updateItemsStatus({ isEnabled: event.target.checked })
          }
          data-testid="test-id-category-items-enable-checkbox"
          checked={
            category.items.filter((item) => !item.isEnabled).length === 0
          }
        />
      }
      label={
        <Typography variant="caption" color="text.secondary">
          Enable all
        </Typography>
      }
      sx={{ mr: 0 }}
    />
  );
};

export const UpdateMenu = (): JSX.Element => {
  const { categories, currency, isItemWiseTaxEnabled } = useMenuData();
  const [selectedCategory, setSelectedCategory] = useState<
    CategoryType | undefined
  >(categories[0]);

  const [settingsAnchor, setSettingsAnchor] =
    useState<HTMLButtonElement | null>(null);

  const categoryItems = useMemo(() => {
    if (selectedCategory == undefined) return [];
    if (selectedCategory?.items == undefined) return [];
    return selectedCategory?.items;
  }, [selectedCategory]);

  useEffect(() => {
    if (selectedCategory == undefined && categories.length >= 1) {
      setSelectedCategory(categories[0]);
      return;
    }

    const newCategory = categories.find(
      (category) => category.id === selectedCategory?.id
    );

    if (JSON.stringify(newCategory) !== JSON.stringify(selectedCategory)) {
      setSelectedCategory(newCategory);
    }
  }, [categories, selectedCategory]);

  return (
    <Box
      bgcolor="rgb(245, 245, 245)"
      height="calc(100vh - 60px)"
      p={2}
      boxSizing="border-box"
      display="flex"
      flexDirection="column"
      gap={1.5}
    >
      <GoLiveChecklist />

      <Box
        display="flex"
        justifyContent="flex-end"
        alignItems="center"
        gap={1}
        flexShrink={0}
      >
        <Tooltip title="Menu settings">
          <IconButton
            size="small"
            onClick={(e) => setSettingsAnchor(e.currentTarget)}
          >
            <SettingsIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Popover
          open={Boolean(settingsAnchor)}
          anchorEl={settingsAnchor}
          onClose={() => setSettingsAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Box sx={{ p: 2, minWidth: 200 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              mb={1}
            >
              Menu settings
            </Typography>
            <UpdateCurrency />
          </Box>
        </Popover>
        <CustomerFacingMenuLink />
      </Box>

      <Paper
        variant="outlined"
        sx={{
          flex: 1,
          display: "flex",
          overflow: "hidden",
          borderRadius: 2,
          minHeight: 0,
        }}
      >
        <Box
          width="28%"
          display="flex"
          flexDirection="column"
          borderRight="1px solid rgba(0,0,0,0.12)"
        >
          <Box
            px={2}
            py={1.25}
            borderBottom="1px solid rgba(0,0,0,0.08)"
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexShrink={0}
          >
            <Typography
              variant="subtitle2"
              fontWeight={600}
              color="text.primary"
            >
              Categories
            </Typography>
            <Typography variant="caption" color="text.disabled">
              {categories.length}
            </Typography>
          </Box>

          <Box flex={1} overflow="auto">
            {categories.length === 0 ? (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                py={5}
                px={2}
                color="text.secondary"
                textAlign="center"
              >
                <CategoryIcon sx={{ fontSize: 36, mb: 1, opacity: 0.25 }} />
                <Typography variant="body2">No categories yet.</Typography>
                <Typography variant="caption">Add your first below.</Typography>
              </Box>
            ) : (
              <DashboardCategories
                onSelectedCategoryChange={setSelectedCategory}
                categories={categories}
                selectedCategory={selectedCategory}
              />
            )}
          </Box>

          <Box
            px={2}
            py={1.5}
            borderTop="1px solid rgba(0,0,0,0.08)"
            flexShrink={0}
          >
            <AddCategory />
          </Box>
        </Box>

        <Box width="72%" display="flex" flexDirection="column">
          <Box
            px={2}
            py={1.25}
            borderBottom="1px solid rgba(0,0,0,0.08)"
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexShrink={0}
          >
            {selectedCategory ? (
              <>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    {selectedCategory.name}
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    {categoryItems.length} item
                    {categoryItems.length !== 1 ? "s" : ""}
                  </Typography>
                </Box>
                <EnableAllSwitch category={selectedCategory} />
              </>
            ) : (
              <Typography variant="subtitle2" color="text.disabled">
                Items
              </Typography>
            )}
          </Box>

          <Box flex={1} overflow="auto">
            {selectedCategory === undefined ? (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                height="100%"
                color="text.disabled"
                textAlign="center"
                px={4}
              >
                <Typography variant="body2">
                  Select a category to manage its items
                </Typography>
              </Box>
            ) : (
              <CategoryItems
                currency={currency}
                categoryItems={categoryItems}
                isItemWiseTaxEnabled={isItemWiseTaxEnabled}
              />
            )}
          </Box>

          {selectedCategory && (
            <Box
              px={2}
              py={1.5}
              borderTop="1px solid rgba(0,0,0,0.08)"
              flexShrink={0}
            >
              <AddItem categoryId={selectedCategory?.id} />
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
};
